import React, { useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import { Check, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Searchable single-select dropdown. `options` is an array of either strings
 * or `{ value, label }` objects. Filters by typed text and shows up to all
 * results in a scrollable list.
 */
export default function Combobox({
  value,
  onChange,
  options = [],
  placeholder = "Select...",
  emptyText = "No matches.",
  disabled = false,
  testId,
  className = "",
}) {
  const [open, setOpen] = useState(false);
  const norm = (o) => (typeof o === "string" ? { value: o, label: o } : o);
  const opts = options.map(norm);
  const selected = opts.find((o) => o.value === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          data-testid={testId}
          aria-expanded={open}
          className={cn(
            "flex items-center justify-between w-full h-10 px-3 text-sm bg-white border border-zinc-200 mt-1.5",
            "hover:border-zinc-400 focus:outline-none focus:border-[#002FA7] disabled:bg-zinc-50 disabled:text-zinc-400 disabled:cursor-not-allowed",
            "rounded-none transition-colors",
            className,
          )}
        >
          <span className={cn("truncate text-left", !selected && "text-zinc-400")}>
            {selected ? selected.label : placeholder}
          </span>
          <ChevronDown className="w-4 h-4 text-zinc-400 shrink-0 ml-2" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="p-0 rounded-none w-[var(--radix-popover-trigger-width)]"
        align="start"
      >
        <Command className="rounded-none">
          <CommandInput placeholder="Type to search..." className="h-10" data-testid={testId ? `${testId}-input` : undefined} />
          <CommandList className="max-h-72">
            <CommandEmpty>{emptyText}</CommandEmpty>
            <CommandGroup>
              {opts.map((o) => (
                <CommandItem
                  key={o.value}
                  value={o.label}
                  onSelect={() => {
                    onChange(o.value);
                    setOpen(false);
                  }}
                  className="rounded-none cursor-pointer text-sm"
                  data-testid={testId ? `${testId}-opt-${o.value}` : undefined}
                >
                  <Check className={cn("mr-2 h-3.5 w-3.5", value === o.value ? "opacity-100 text-[#002FA7]" : "opacity-0")} />
                  <span className="metric">{o.label}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
